/**
 * Ficha de inquilino — `/inquilinos/:id`.
 *
 * La pantalla más importante del encargo, y la que no existía en la fase 1. Toda la
 * información llega en UNA sola petición (`GET /tenants/:id`), a propósito: repartirla en seis
 * consultas haría que la ficha se pintara a trozos durante un segundo, que es justo la
 * sensación que este proyecto evita en todas las demás pantallas.
 *
 * Estructura: cabecera con acciones, columna izquierda con los datos personales y columna
 * derecha con pestañas (contratos, pagos, documentos, notas, actividad). La pestaña activa va
 * en la URL, igual que en el panel de inicio, para que un enlace sea compartible.
 */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LeaseChainEntry,
  LeaseExpiry,
  LeaseSummary,
  TenantDetailResponse,
  TenantDocumentCategory,
  TenantDocumentSlot,
  TenantLeaseChange,
  TenantRentChange,
  TimelineEntry,
} from '@naar/shared';
import {
  TENANT_DOCUMENT_LABELS,
  formatIban,
  TERMINATION_REASON_LABELS,
  PREFERRED_LANGUAGE_LABELS,
  isLeaseChangeCategory,
} from '@naar/shared';
import { api, queryKeys, ApiError } from '../api/client';
import { formatEurCents, formatShortDate } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { Card, CardHeader } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Avatar } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { useAuth } from '../auth/AuthContext';
import { toast } from '../components/feedback/Toaster';
import { ConfirmDialog } from '../components/overlay/Drawer';
import styles from './TenantDetailPage.module.css';
import { LeaseActions, useLeaseInvalidation } from './leases/LeaseActions';
import { FileDrop } from './ledger/FileDrop';
import { TenantActions } from './tenants/TenantActions';
import { ViewDocumentButton } from '../components/overlay/DocumentViewer';

const TABS = [
  { key: 'contratos', label: 'Contratos' },
  { key: 'pagos', label: 'Pagos' },
  { key: 'documentos', label: 'Documentos' },
  { key: 'notas', label: 'Notas' },
  { key: 'actividad', label: 'Actividad' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const PAYMENT_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
  al_dia: 'success',
  pendiente: 'warning',
  impago: 'danger',
};

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabKey | null) ?? 'contratos';

  const query = useQuery({
    queryKey: queryKeys.tenant(id),
    queryFn: () => api.tenant(id),
    enabled: Boolean(id),
  });

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Inquilino" subtitle="Cargando ficha…" />
        <div className={styles.layout}>
          <Skeleton height={320} />
          <Skeleton height={420} />
        </div>
      </>
    );
  }

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <>
        <PageHeader title="Inquilino" />
        {notFound ? (
          <EmptyState
            icon="users"
            title="Ese inquilino no existe"
            body="Puede que se haya borrado o que el enlace sea antiguo."
            actionLabel="Volver a Inquilinos"
            onAction={() => window.history.back()}
          />
        ) : (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        )}
      </>
    );
  }

  const tenant = query.data;

  return (
    <>
      <TenantHeader tenant={tenant} />

      <div className={styles.layout}>
        <PersonalColumn tenant={tenant} />

        <div className={styles.rightColumn}>
          <nav className={styles.tabs} role="tablist">
            {TABS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={tab === entry.key}
                className={[styles.tab, tab === entry.key ? styles.tabActive : ''].filter(Boolean).join(' ')}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set('tab', entry.key);
                  setSearchParams(next, { replace: true });
                }}
              >
                {entry.label}
              </button>
            ))}
          </nav>

          {tab === 'contratos' && <ContractsTab tenant={tenant} />}
          {tab === 'pagos' && <PaymentsTab tenant={tenant} />}
          {tab === 'documentos' && <DocumentsTab tenant={tenant} />}
          {tab === 'notas' && <NotesTab tenant={tenant} />}
          {tab === 'actividad' && <ActivityTab tenantId={tenant.id} />}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Cabecera
// ---------------------------------------------------------------------------

function TenantHeader({ tenant }: { tenant: TenantDetailResponse }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const invite = useMutation({
    mutationFn: () => api.invitePortal(tenant.id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
      toast.success(`Invitación creada para ${result.email}.`, {
        label: 'Copiar enlace',
        run: () => void navigator.clipboard.writeText(`${window.location.origin}${result.inviteUrl}`),
      });
    },
    onError: (error: unknown) => toast.error(error instanceof Error ? error.message : 'No se ha podido invitar.'),
  });

  const draft = tenant.draftLease;
  const location = tenant.currentLease
    ? `${tenant.currentLease.property.name} · ${tenant.currentLease.unit.code}`
    : draft
      ? `${draft.property.name} · ${draft.unit.code} · contrato sin activar`
      : 'Inactivo · no ocupa ninguna habitación';

  return (
    <div className={styles.header}>
      <Avatar initials={tenant.initials} size={54} />

      <div className={styles.headerMain}>
        <div className={styles.headerTop}>
          <h1 className={styles.name}>{tenant.fullName}</h1>
          {/*
            El estado va PRIMERO y con sus propias palabras: es lo que se mira al abrir la
            ficha. «Al día» habla del dinero y no de si la persona sigue viviendo ahí — se puede
            estar al día y haberse ido hace tres meses.
          */}
          <Badge tone={tenant.currentLease ? 'success' : draft ? 'warning' : 'neutral'}>
            {tenant.currentLease ? 'Activo' : draft ? 'Contrato en borrador' : 'Inactivo'}
          </Badge>
          <Badge tone={PAYMENT_TONE[tenant.paymentStatus] ?? 'success'}>
            {tenant.paymentStatusLabel}
          </Badge>
          {tenant.deletedAt && <Badge tone="warning">Dado de baja</Badge>}
        </div>
        <p className={styles.headerMeta}>
          {location}
          {tenant.idDocument && <> · {tenant.idDocumentType.toUpperCase()} {tenant.idDocument}</>}
          {Number(tenant.balanceDue) > 0 && (
            <> · <strong className={styles.due}>{formatEurCents(tenant.balanceDue)} pendiente</strong></>
          )}
        </p>
      </div>

      <div className={styles.headerActions}>
        {can('portal:invite') && !tenant.portalAccess && !tenant.deletedAt && (
          <Button variant="secondary" size="sm" onClick={() => invite.mutate()} disabled={invite.isPending}>
            {invite.isPending ? 'Invitando…' : 'Invitar al portal'}
          </Button>
        )}
        {tenant.portalAccess && (
          <span className={styles.portalBadge} title={`Acceso: ${tenant.portalAccess.email}`}>
            <Icon name="users" size={13} /> Portal activo
          </span>
        )}

        <TenantActions tenant={tenant} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Columna izquierda
// ---------------------------------------------------------------------------

function DataRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className={styles.dataRow}>
      <span className={styles.dataLabel}>{label}</span>
      <span className={value ? styles.dataValue : styles.dataEmpty}>{value || 'Sin informar'}</span>
    </div>
  );
}

function PersonalColumn({ tenant }: { tenant: TenantDetailResponse }) {
  const idExpired =
    tenant.idDocumentExpiresAt !== null && new Date(tenant.idDocumentExpiresAt) < new Date();

  return (
    <div className={styles.leftColumn}>
      <Card>
        <CardHeader title="Datos personales" />
        <div className={styles.dataList}>
          <DataRow label="Documento" value={tenant.idDocument ? `${tenant.idDocumentType.toUpperCase()} ${tenant.idDocument}` : null} />
          <div className={styles.dataRow}>
            <span className={styles.dataLabel}>Caducidad</span>
            <span className={idExpired ? styles.dataDanger : styles.dataValue}>
              {tenant.idDocumentExpiresAt ? formatShortDate(tenant.idDocumentExpiresAt) : 'Sin informar'}
              {idExpired && ' · caducado'}
            </span>
          </div>
          <DataRow label="Nacionalidad" value={tenant.nationality} />
          <DataRow label="Nacimiento" value={tenant.birthDate ? formatShortDate(tenant.birthDate) : null} />
          <DataRow label="Email" value={tenant.email} />
          <DataRow label="Teléfono" value={tenant.phone} />
          <DataRow label="Idioma" value={PREFERRED_LANGUAGE_LABELS[tenant.preferredLanguage]} />
        </div>
      </Card>

      <Card>
        <CardHeader title="Domiciliación y RGPD" />
        <div className={styles.dataList}>
          <DataRow
            label="IBAN"
            value={
              tenant.iban
                ? formatIban(tenant.iban)
                : tenant.ibanLast4
                  ? `•••• ${tenant.ibanLast4}`
                  : null
            }
          />
          <DataRow label="Mandato SEPA" value={tenant.mandateReference} />
          <DataRow label="Firmado" value={tenant.mandateSignedAt ? formatShortDate(tenant.mandateSignedAt) : null} />
          <DataRow
            label="Consentimiento"
            value={tenant.marketingConsentAt ? `Sí · ${formatShortDate(tenant.marketingConsentAt.slice(0, 10))}` : 'No prestado'}
          />
          <DataRow
            label="Conservación"
            value={tenant.dataRetentionUntil ? `Hasta ${formatShortDate(tenant.dataRetentionUntil)}` : null}
          />
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña: contratos
// ---------------------------------------------------------------------------

function LeaseCard({ lease, actions = true }: { lease: LeaseSummary; actions?: boolean }) {
  return (
    <div className={styles.leaseCard}>
      <div className={styles.leaseTop}>
        <div>
          <Link to={`/propiedades/${lease.property.id}`} className={styles.leaseTitle}>
            {lease.property.name} · {lease.unit.code}
          </Link>
          <p className={styles.leaseDates}>
            Desde {formatShortDate(lease.startDate)}
            {lease.endDate ? ` hasta ${formatShortDate(lease.endDate)}` : ' · indefinido'}
          </p>
        </div>
        <span className={styles.leaseRent}>{formatEurCents(lease.monthlyRent)}<small>/mes</small></span>
      </div>

      <div className={styles.leaseGrid}>
        <div>
          <span className={styles.leaseLabel}>Fianza</span>
          <span className={styles.leaseValue}>
            {formatEurCents(lease.depositAmount)} · {lease.depositStatus}
          </span>
        </div>
        <div>
          <span className={styles.leaseLabel}>Día de pago</span>
          <span className={styles.leaseValue}>{lease.paymentDay}</span>
        </div>
        <div>
          <span className={styles.leaseLabel}>Preaviso</span>
          <span className={styles.leaseValue}>
            {lease.noticePeriodDays !== null ? `${lease.noticePeriodDays} días` : 'Sin pactar'}
          </span>
        </div>
        <div>
          <span className={styles.leaseLabel}>Actualización</span>
          <span className={styles.leaseValue}>
            {lease.indexationType === 'ninguna'
              ? 'Ninguna'
              : `${lease.indexationType.toUpperCase()} ${lease.indexationValue ?? 0} %`}
          </span>
        </div>
      </div>

      {lease.tenants.length > 1 && (
        <p className={styles.solidarity}>
          <Icon name="info" size={12} /> {lease.tenants.length} titulares solidarios:{' '}
          {lease.tenants.map((entry) => entry.fullName).join(', ')}
        </p>
      )}

      {/* Activar, renovar y rescindir. El componente decide solo qué ofrecer según el estado
          del contrato y el rol de quien mira. */}
      {actions && <LeaseActions lease={lease} />}

      {lease.terminatedAt && (
        <p className={styles.terminated}>
          Rescindido el {formatShortDate(lease.terminatedAt)}
          {lease.terminationReason && ` · ${TERMINATION_REASON_LABELS[lease.terminationReason]}`}
        </p>
      )}
    </div>
  );
}

/** Cadena de renovaciones como línea temporal. */
function LeaseChain({ chain }: { chain: LeaseChainEntry[] }) {
  if (chain.length < 2) return null;

  return (
    <Card>
      <CardHeader
        title="Cadena de renovaciones"
        subtitle="Cada renovación es un contrato nuevo: el anterior sobrevive porque respalda los cobros ya emitidos."
      />
      <ol className={styles.chain}>
        {chain.map((link) => (
          <li key={link.id} className={link.isCurrent ? styles.chainCurrent : undefined}>
            <span className={styles.chainDot} aria-hidden="true" />
            <div className={styles.chainBody}>
              <span className={styles.chainDates}>
                {formatShortDate(link.startDate)} — {link.endDate ? formatShortDate(link.endDate) : 'indefinido'}
              </span>
              <span className={styles.chainRent}>
                {formatEurCents(link.monthlyRent)}
                {link.rentChangePercent !== null && (
                  <em className={link.rentChangePercent >= 0 ? styles.chainUp : styles.chainDown}>
                    {link.rentChangePercent >= 0 ? '+' : ''}
                    {link.rentChangePercent} %
                  </em>
                )}
              </span>
              <Badge tone={link.isCurrent ? 'success' : 'neutral'}>{link.status}</Badge>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** El aviso de contrato que se acaba, con las mismas palabras en Contratos y en Documentos. */
function expiryMessage(expiry: LeaseExpiry): string {
  const end = formatShortDate(expiry.endDate);
  const days = expiry.daysToEnd;
  if (days < 0) return `El contrato venció el ${end} y no hay renovación ni rescisión subida.`;
  if (days === 0) return 'El contrato termina hoy y no hay renovación ni rescisión subida.';
  return `Al contrato le ${days === 1 ? 'queda 1 día' : `quedan ${days} días`}: vence el ${end} y no hay renovación ni rescisión subida.`;
}

function ContractsTab({ tenant }: { tenant: TenantDetailResponse }) {
  /*
   * Las renovaciones subidas como hoja esperan en borrador encadenadas al vigente, una detrás de
   * otra: son los contratos que vienen, no historia.
   */
  const upcoming: LeaseSummary[] = [];
  let cursor = tenant.currentLease;
  while (cursor?.renewedByLeaseId) {
    const nextId = cursor.renewedByLeaseId;
    const next = tenant.leases.find((lease) => lease.id === nextId && lease.status === 'borrador');
    if (!next || upcoming.includes(next)) break;
    upcoming.push(next);
    cursor = next;
  }
  const draft = tenant.draftLease;
  const shown = new Set([tenant.currentLease?.id, draft?.id, ...upcoming.map((lease) => lease.id)]);
  const historic = tenant.leases.filter((lease) => !shown.has(lease.id));

  return (
    <div className={styles.tabBody}>
      {tenant.currentLease ? (
        <Card>
          <CardHeader title="Contrato vigente" />
          {tenant.leaseExpiry && (
            <p className={styles.expiryWarning} role="note">
              <Icon name="info" size={13} />
              {expiryMessage(tenant.leaseExpiry)} Súbela en «Documentos».
            </p>
          )}
          <LeaseCard lease={tenant.currentLease} />
        </Card>
      ) : draft ? (
        <Card>
          <CardHeader
            title="Contrato sin activar"
            subtitle="Está en borrador: no emite sus cobros ni ocupa la habitación, y por eso no cuenta como vigente. Al activarlo verás antes los cargos que se emiten desde su inicio."
          />
          <LeaseCard lease={draft} />
        </Card>
      ) : (
        <Card>
          <EmptyState
            title="Sin contrato vigente"
            body="Este inquilino no tiene ningún contrato activo ahora mismo."
          />
        </Card>
      )}

      {upcoming.map((lease, index) => {
        const isLast = index === upcoming.length - 1;
        return (
          <Card key={lease.id}>
            <CardHeader
              title={upcoming.length > 1 ? `${index + 1}.ª renovación firmada` : 'Renovación firmada'}
              subtitle={[
                index === 0
                  ? `Empieza el ${formatShortDate(lease.startDate)} y se activa sola cuando le toque; hasta entonces sigue el contrato vigente.`
                  : `Empieza el ${formatShortDate(lease.startDate)}, detrás de la anterior, y se activa sola cuando le toque.`,
                isLast
                  ? 'Para deshacerla, borra la hoja en Documentos.'
                  : 'Para deshacerla, borra antes las que van detrás.',
              ].join(' ')}
            />
            {/* Solo la primera se puede adelantar a mano: las demás esperan a la de delante. */}
            <LeaseCard lease={lease} actions={index === 0} />
          </Card>
        );
      })}

      <LeaseChain chain={tenant.leaseChain} />

      {historic.length > 0 && (
        <Card>
          <CardHeader title="Histórico" subtitle={`${historic.length} contrato${historic.length === 1 ? '' : 's'} anterior${historic.length === 1 ? '' : 'es'}`} />
          <div className={styles.leaseList}>
            {historic.map((lease) => (
              <LeaseCard key={lease.id} lease={lease} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña: pagos
// ---------------------------------------------------------------------------

function PaymentsTab({ tenant }: { tenant: TenantDetailResponse }) {
  return (
    <div className={styles.tabBody}>
      <Card>
        <CardHeader
          title="Saldo"
          subtitle="Los cobros se gestionan en la pantalla de Cobros; aquí es solo lectura."
        />
        <div className={styles.balance}>
          <span className={styles.balanceLabel}>Pendiente de cobro</span>
          <span className={Number(tenant.balanceDue) > 0 ? styles.balanceDue : styles.balanceOk}>
            {formatEurCents(tenant.balanceDue)}
          </span>
        </div>
        <p className={styles.balanceHint}>
          <Link to="/seguimiento">Ver el rent roll completo →</Link>
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña: documentos
// ---------------------------------------------------------------------------

/** Pulsar el nombre lo descarga (queda registrado); si falla, se dice en vez de no hacer nada. */
function descargar(id: string, name: string) {
  api
    .downloadDocument(id, name)
    .catch((error: unknown) =>
      toast.error(error instanceof Error ? error.message : 'No se ha podido descargar el documento.'),
    );
}

const categoryLabel = (category: string) =>
  TENANT_DOCUMENT_LABELS[category as TenantDocumentCategory] ?? category;

/**
 * Cada papel esperado lleva su sitio para soltarlo, al lado de lo que falta: «el pasaporte va
 * aquí». Subir otro NO borra el anterior: el sitio enseña el último y los demás pasan a
 * «Documentos anteriores», que es como queda constancia de un contrato renovado. «Otros» es la
 * excepción: admite varios y los enseña todos debajo.
 */
function DocumentsTab({ tenant }: { tenant: TenantDetailResponse }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const invalidateLeases = useLeaseInvalidation();
  const canUpload = can('tenant:update') && !tenant.deletedAt;
  const [subiendo, setSubiendo] = useState<string | null>(null);

  async function subir(slot: TenantDocumentSlot, file: File) {
    setSubiendo(slot.category);
    try {
      const result = await api.uploadTenantDocument(tenant.id, slot.category as TenantDocumentCategory, file);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
      // El listado enseña los distintivos de documentación de cada ficha.
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      if (result.leaseChange) {
        // La hoja ha cambiado el contrato: cobros, ocupación y el resto del panel también.
        invalidateLeases();
        toast.success(`${slot.label}: ${describeLeaseChange(result.leaseChange)}.`);
      } else {
        // Un DNI o pasaporte en una ficha sin fecha de nacimiento se lee (OCR local) y la rellena.
        const nacimiento = result.birthDate
          ? ` Fecha de nacimiento leída del documento: ${formatShortDate(result.birthDate)}.`
          : '';
        // La renta del contrato manda: cambia cobros, la habitación y medio panel.
        const renta = result.rentChange ? ` ${describeRentChange(result.rentChange)}` : '';
        if (result.rentChange) invalidateLeases();
        toast.success(`${slot.label}: «${file.name}» guardado.${nacimiento}${renta}`);
      }
      if (result.warnings && result.warnings.length > 0) toast.info(result.warnings.join(' '));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se ha podido subir el documento.');
    } finally {
      setSubiendo(null);
    }
  }

  const expected = tenant.documents.filter((slot) => !isLeaseChangeCategory(slot.category));
  const leaseChanges = tenant.documents.filter((slot) => isLeaseChangeCategory(slot.category));

  return (
    <div className={styles.tabBody}>
      <LeaseChangeSlots
        tenant={tenant}
        slots={leaseChanges}
        canUpload={canUpload}
        busy={subiendo}
        onFile={subir}
      />

      <Card>
        <CardHeader
          title="Documentación esperada"
          subtitle="El estado se DERIVA de los documentos subidos: suelta cada papel en su sitio y deja de faltar."
        />
        <div className={styles.slotList}>
          {expected.map((slot) =>
            slot.category === 'Otros' ? (
              <OtherDocumentsSlot
                key={slot.category}
                slot={slot}
                canUpload={canUpload}
                busy={subiendo === slot.category}
                onFile={(file) => void subir(slot, file)}
                tenantId={tenant.id}
              />
            ) : (
            <div key={slot.category} className={styles.slot}>
              <span className={styles.slotIcon} data-present={slot.present}>
                <Icon name={slot.present ? 'file-lines' : 'file'} size={15} />
              </span>
              <div className={styles.slotBody}>
                <span className={styles.slotLabel}>{slot.label}</span>
                <span className={styles.slotMeta}>
                  {slot.document ? (
                    <>
                      <button
                        type="button"
                        className={styles.slotLink}
                        onClick={() => descargar(slot.document!.id, slot.document!.name)}
                        title="Descargar"
                      >
                        {slot.document.name}
                      </button>
                      {` · ${formatShortDate(slot.document.uploadedAt.slice(0, 10))}`}
                    </>
                  ) : (
                    'Pendiente de aportar'
                  )}
                </span>
              </div>
              <div className={styles.slotActions}>
                {slot.expired ? (
                  <Badge tone="danger">Caducado</Badge>
                ) : slot.present ? (
                  <Badge tone="success">Aportado</Badge>
                ) : (
                  <Badge tone="neutral">Falta</Badge>
                )}
                {canUpload && (
                  <FileDrop
                    label={slot.present ? 'Subir otro' : 'Subir'}
                    busy={subiendo === slot.category}
                    onFile={(file) => void subir(slot, file)}
                  />
                )}
                {/* El ojo lo abre en el visor y la papelera lo quita. El nombre lo descarga. */}
                {slot.document && <ViewDocumentButton document={{ id: slot.document.id, name: slot.document.name }} />}
                {slot.document && canUpload && (
                  <BorrarDocumento tenantId={tenant.id} document={slot.document} etiqueta={slot.label} />
                )}
              </div>
            </div>
            ),
          )}
        </div>
      </Card>

      {tenant.otherDocuments.length > 0 && (
        <Card>
          <CardHeader
            title="Documentos anteriores"
            subtitle="Los que estaban en su sitio antes de subir otro. No se borran: quedan como constancia."
          />
          <div className={styles.slotList}>
            {tenant.otherDocuments.map((document) => (
              <div key={document.id} className={styles.slot}>
                <span className={styles.slotIcon} data-present="true">
                  <Icon name="file-lines" size={15} />
                </span>
                <div className={styles.slotBody}>
                  <button
                    type="button"
                    className={`${styles.slotLabel} ${styles.slotLink}`}
                    onClick={() => descargar(document.id, document.name)}
                    title="Descargar"
                  >
                    {document.name}
                  </button>
                  <span className={styles.slotMeta}>
                    {categoryLabel(document.category)} ·{' '}
                    {formatShortDate(document.uploadedAt.slice(0, 10))}
                  </span>
                </div>
                <div className={styles.slotActions}>
                  <ViewDocumentButton document={{ id: document.id, name: document.name }} />
                  {canUpload && (
                    <BorrarDocumento tenantId={tenant.id} document={document} etiqueta={categoryLabel(document.category)} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * «Otros»: el único sitio que admite varios papeles. No falta nunca —no se pide nada en
 * concreto—, así que no lleva «Falta»; subir uno lo AÑADE a la lista, no sustituye al anterior.
 */
function OtherDocumentsSlot({
  slot,
  canUpload,
  busy,
  onFile,
  tenantId,
}: {
  slot: TenantDocumentSlot;
  canUpload: boolean;
  busy: boolean;
  onFile: (file: File) => void;
  tenantId: string;
}) {
  const documents = slot.documents ?? [];
  return (
    <div className={styles.slotGroup}>
      <div className={styles.slot}>
        <span className={styles.slotIcon} data-present={slot.present}>
          <Icon name={slot.present ? 'file-lines' : 'file'} size={15} />
        </span>
        <div className={styles.slotBody}>
          <span className={styles.slotLabel}>{slot.label}</span>
          <span className={styles.slotMeta}>
            {documents.length === 0
              ? 'Cualquier otro papel del inquilino. Se pueden subir varios.'
              : documents.length === 1
                ? '1 documento'
                : `${documents.length} documentos`}
          </span>
        </div>
        <div className={styles.slotActions}>
          {canUpload && <FileDrop label={documents.length > 0 ? 'Añadir otro' : 'Subir'} busy={busy} onFile={onFile} />}
        </div>
      </div>
      {documents.length > 0 && (
        <ul className={styles.slotFiles}>
          {documents.map((document) => (
            <li key={document.id} className={styles.slotFile}>
              <Icon name="file-lines" size={14} />
              <span className={styles.slotFileName}>
                <button
                  type="button"
                  className={styles.slotLink}
                  onClick={() => descargar(document.id, document.name)}
                  title="Descargar"
                >
                  {document.name}
                </button>
                <span className={styles.slotMeta}>{` · ${formatShortDate(document.uploadedAt.slice(0, 10))}`}</span>
              </span>
              <ViewDocumentButton document={{ id: document.id, name: document.name }} />
              {canUpload && <BorrarDocumento tenantId={tenantId} document={document} etiqueta={slot.label} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * La papelera de un documento del inquilino, junto al ojo. Pide confirmación y lo quita de la
 * ficha (borrado lógico: el fichero se conserva en el almacén, como el resto de documentos).
 * Si el servidor no deja —una rescisión ya aplicada, por ejemplo— lo dice con su mensaje.
 */
function BorrarDocumento({
  tenantId,
  document,
  etiqueta,
}: {
  tenantId: string;
  document: { id: string; name: string };
  etiqueta: string;
}) {
  const queryClient = useQueryClient();
  const invalidateLeases = useLeaseInvalidation();
  const [confirmando, setConfirmando] = useState(false);

  const borrar = useMutation({
    mutationFn: () => api.deleteTenantDocument(tenantId, document.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      // Un contrato quitado puede cambiar lo que enseñan Contratos y Cobros.
      invalidateLeases();
      toast.success(`«${document.name}» quitado.`);
      setConfirmando(false);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido quitar el documento.');
      setConfirmando(false);
    },
  });

  return (
    <>
      <button
        type="button"
        className={styles.borrarDoc}
        onClick={() => setConfirmando(true)}
        disabled={borrar.isPending}
        aria-label={`Quitar «${document.name}»`}
        title="Quitar el documento"
      >
        <Icon name="trash" size={15} strokeWidth={2} />
      </button>
      {confirmando && (
        <ConfirmDialog
          title="Quitar el documento"
          body={`«${document.name}» (${etiqueta}) deja de verse en la ficha. El fichero no se borra del almacén.`}
          confirmLabel="Quitar"
          tone="danger"
          onCancel={() => setConfirmando(false)}
          onConfirm={async () => {
            await borrar.mutateAsync().catch(() => undefined);
          }}
        />
      )}
    </>
  );
}

/** «Renta de H2 leída del contrato: 390,00 €/mes (antes 400,00 €); 3 cargos corregidos.» */
function describeRentChange(change: TenantRentChange): string {
  const cargos =
    change.chargesUpdated > 0
      ? `; ${change.chargesUpdated} ${change.chargesUpdated === 1 ? 'cargo corregido' : 'cargos corregidos'}`
      : '';
  return `Renta de ${change.unitCode} leída del contrato: ${formatEurCents(change.to)}/mes (antes ${formatEurCents(change.from)})${cargos}.`;
}

/** «H2 se renueva desde el 01/08/26 hasta el 30/06/27 · 397,00 €/mes». */
function describeLeaseChange(change: TenantLeaseChange): string {
  if (change.kind === 'renovacion') {
    const until = change.endDate ? ` hasta el ${formatShortDate(change.endDate)}` : '';
    const rent = change.monthlyRent ? ` · ${formatEurCents(change.monthlyRent)}/mes` : '';
    return change.state === 'pendiente'
      ? `${change.unitCode} se renueva desde el ${formatShortDate(change.effectiveAt)}${until}${rent}`
      : `${change.unitCode} renovado${until}${rent}`;
  }

  const head =
    change.state === 'pendiente'
      ? `${change.unitCode} se rescinde el ${formatShortDate(change.effectiveAt)}`
      : `${change.unitCode} rescindido el ${formatShortDate(change.effectiveAt)}`;
  const deposit = [
    change.depositAmount ? `fianza ${formatEurCents(change.depositAmount)}` : null,
    change.depositRefund ? `se devuelven ${formatEurCents(change.depositRefund)}` : null,
  ].filter(Boolean);
  return [head, ...deposit].join(' · ');
}

const LEASE_CHANGE_HINT: Record<string, string> = {
  Renovacion: 'La hoja firmada alarga el contrato hasta la fecha que pone, con su renta.',
  Rescision: 'La hoja firmada termina el contrato el día que pone.',
};

/**
 * Los dos sitios que no guardan un papel sin más: la hoja se LEE y cambia el contrato vigente
 * (ver `services/leaseDocuments.ts`). Por eso van aparte de la documentación esperada y con sus
 * propios permisos —renovar y rescindir—, y por eso borrar aquí deshace lo que hizo la hoja.
 * La renovación lleva una fila POR HOJA, porque se encadenan: la siguiente se sube desde la última.
 */
function LeaseChangeSlots({
  tenant,
  slots,
  canUpload,
  busy,
  onFile,
}: {
  tenant: TenantDetailResponse;
  slots: TenantDocumentSlot[];
  canUpload: boolean;
  busy: string | null;
  onFile: (slot: TenantDocumentSlot, file: File) => Promise<void>;
}) {
  const { can } = useAuth();
  const invalidate = useLeaseInvalidation();
  const [toTerminate, setToTerminate] = useState<{ slot: TenantDocumentSlot; file: File } | null>(null);
  const [toDelete, setToDelete] = useState<TenantDocumentSlot | null>(null);

  const lease = tenant.currentLease;
  /*
   * Sin contrato vigente, las hojas ANTIGUAS se siguen pudiendo subir. Una renovación renueva el
   * último contrato que terminó (o el que estaba en vigor justo antes de la hoja) y queda
   * terminada si ya acabó. Una rescisión termina el contrato que estaba en vigor el día que se
   * fue, y si ese contrato ya acabaría después, queda rescindido ese día.
   */
  const lastEnded = lease ? null : (tenant.leases.find((other) => other.status === 'vencido') ?? null);
  const uploadable = () => Boolean(lease || lastEnded);
  const allowed = (slot: TenantDocumentSlot) =>
    canUpload && can(slot.category === 'Renovacion' ? 'lease:renew' : 'lease:terminate');
  // Hay una fila por hoja de renovación; la siguiente se sube desde la última.
  const lastRenewal = slots.filter((slot) => slot.category === 'Renovacion').at(-1);

  const remove = useMutation({
    mutationFn: (slot: TenantDocumentSlot) => api.deleteTenantDocument(tenant.id, slot.document!.id),
    onSuccess: (_, slot) => {
      invalidate();
      toast.info(
        slot.category === 'Renovacion'
          ? 'Renovación deshecha: el contrato vuelve a terminar cuando terminaba.'
          : slot.leaseChange?.state === 'pendiente'
            ? 'Rescisión cancelada: el contrato sigue.'
            : 'Hoja borrada.',
      );
      setToDelete(null);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof Error ? error.message : 'No se ha podido borrar la hoja.'),
  });

  // El mismo aviso que la alerta: lo calcula la API con la regla de `leaseExpiry.ts`.
  const expiry = lease ? tenant.leaseExpiry : null;

  return (
    <Card>
      <CardHeader
        title="Renovación y rescisión"
        subtitle={
          lease
            ? `Contrato vigente: ${lease.unit.code}${lease.endDate ? ` hasta el ${formatShortDate(lease.endDate)}` : ', indefinido'}. Sube la hoja firmada en PDF y el contrato cambia solo en la fecha que pone; con una renovación ya subida, la siguiente va detrás.`
            : tenant.draftLease
              ? `El contrato de ${tenant.draftLease.unit.code} está en borrador: actívalo en Contratos y podrás subir aquí su renovación o rescisión.`
              : lastEnded
                ? `Sin contrato vigente: el de ${lastEnded.unit.code} terminó${lastEnded.endDate ? ` el ${formatShortDate(lastEnded.endDate)}` : ''}. Puedes subir sus hojas antiguas: cada renovación se encadena por fechas al contrato que renueva, y la rescisión lo deja terminado el día que se fue.`
                : 'Sin contrato vigente: no hay nada que renovar ni rescindir.'
        }
      />

      {expiry && (
        <p className={styles.expiryWarning}>
          <Icon name="info" size={13} />
          {expiryMessage(expiry)}
        </p>
      )}

      <div className={styles.slotList}>
        {slots.map((slot) => {
          const change = slot.leaseChange;
          const mayChange = allowed(slot);
          const uploadsHere = slot.category !== 'Renovacion' || slot === lastRenewal;
          return (
            <div key={slot.document?.id ?? slot.category} className={styles.slot}>
              <span className={styles.slotIcon} data-present={slot.present}>
                <Icon name={slot.present ? 'file-lines' : 'file'} size={15} />
              </span>
              <div className={styles.slotBody}>
                <span className={styles.slotLabel}>{slot.label}</span>
                <span className={styles.slotMeta}>
                  {slot.document ? (
                    <>
                      <button
                        type="button"
                        className={styles.slotLink}
                        onClick={() => descargar(slot.document!.id, slot.document!.name)}
                        title="Descargar"
                      >
                        {slot.document.name}
                      </button>
                      {` · ${formatShortDate(slot.document.uploadedAt.slice(0, 10))}`}
                    </>
                  ) : (
                    LEASE_CHANGE_HINT[slot.category]
                  )}
                </span>
                {change && <span className={styles.slotEffect}>{describeLeaseChange(change)}</span>}
              </div>
              <div className={styles.slotActions}>
                {change ? (
                  <Badge tone={change.state === 'pendiente' ? 'warning' : 'success'}>
                    {change.state === 'pendiente' ? 'Programada' : 'Aplicada'}
                  </Badge>
                ) : slot.present ? (
                  <Badge tone="success">Aportado</Badge>
                ) : (
                  <Badge tone="neutral">Sin subir</Badge>
                )}
                {slot.document && mayChange && change?.reversible !== false && (
                  <button type="button" className={styles.slotDelete} onClick={() => setToDelete(slot)}>
                    Borrar
                  </button>
                )}
                {uploadable() && mayChange && uploadsHere && (
                  <FileDrop
                    label={slot.present ? 'Subir otra' : 'Subir'}
                    busy={busy === slot.category}
                    onFile={(file) =>
                      // Rescindir no se deshace: se confirma antes de subir.
                      slot.category === 'Rescision' ? setToTerminate({ slot, file }) : void onFile(slot, file)
                    }
                  />
                )}
                {slot.document && <ViewDocumentButton document={{ id: slot.document.id, name: slot.document.name }} />}
              </div>
            </div>
          );
        })}
      </div>

      {toTerminate && (lease ?? lastEnded) && (
        <ConfirmDialog
          title="Rescindir con esta hoja"
          body={
            lease
              ? `Se lee «${toTerminate.file.name}» y el contrato de ${lease.unit.code} se rescinde el día que pone: en el acto si ya ha pasado. Se anulan los cargos posteriores sin cobrar, se prorratea el del mes y la habitación queda libre. Una rescisión ya aplicada no se deshace borrando la hoja.`
              : `Se lee «${toTerminate.file.name}» y el contrato que estaba en vigor ese día queda rescindido el día que pone. Si ahora vive otra persona en la habitación, sigue igual. Una rescisión ya aplicada no se deshace borrando la hoja.`
          }
          confirmLabel="Subir y rescindir"
          tone="danger"
          onCancel={() => setToTerminate(null)}
          onConfirm={async () => {
            const { slot, file } = toTerminate;
            setToTerminate(null);
            await onFile(slot, file);
          }}
        />
      )}

      {toDelete && (
        <ConfirmDialog
          title={toDelete.category === 'Renovacion' ? 'Deshacer la renovación' : 'Borrar la rescisión'}
          body={
            toDelete.category === 'Renovacion'
              ? 'Se borra la hoja y la renovación con ella: el contrato nuevo desaparece y el anterior vuelve a terminar cuando terminaba. Si ya se había activado, sus cargos sin cobrar se borran.'
              : toDelete.leaseChange?.state === 'pendiente'
                ? 'Se borra la hoja y el contrato sigue: ya no se rescindirá ese día.'
                : 'Se quita la hoja de la ficha; el contrato no cambia.'
          }
          confirmLabel="Borrar la hoja"
          tone="danger"
          onCancel={() => setToDelete(null)}
          onConfirm={async () => {
            await remove.mutateAsync(toDelete);
          }}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pestaña: notas
// ---------------------------------------------------------------------------

function NotesTab({ tenant }: { tenant: TenantDetailResponse }) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [body, setBody] = useState('');

  const create = useMutation({
    mutationFn: (payload: { body: string }) => api.createTenantNote(tenant.id, payload),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
      toast.success('Nota guardada.');
    },
    onError: (error: unknown) =>
      toast.error(error instanceof Error ? error.message : 'No se ha podido guardar la nota.'),
  });

  const remove = useMutation({
    mutationFn: (noteId: string) => api.deleteTenantNote(noteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
      toast.info('Nota borrada.');
    },
  });

  return (
    <div className={styles.tabBody}>
      {can('tenant:update') && !tenant.deletedAt && (
        <Card>
          <CardHeader title="Nueva nota" />
          <form
            className={styles.noteForm}
            onSubmit={(event) => {
              event.preventDefault();
              if (body.trim()) create.mutate({ body: body.trim() });
            }}
          >
            <textarea
              className={styles.noteInput}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Paga siempre el día 3. Tiene un perro pequeño…"
              rows={3}
              maxLength={4000}
            />
            <div className={styles.noteActions}>
              <Button type="submit" size="sm" disabled={!body.trim() || create.isPending}>
                {create.isPending ? 'Guardando…' : 'Añadir nota'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title={`Notas (${tenant.notesList.length})`} />
        {tenant.notesList.length === 0 ? (
          <EmptyState title="Sin notas" body="Las notas del gestor aparecerán aquí." />
        ) : (
          <ul className={styles.noteList}>
            {tenant.notesList.map((note) => (
              <li key={note.id} className={note.pinned ? styles.notePinned : undefined}>
                <p className={styles.noteBody}>{note.body}</p>
                <div className={styles.noteMeta}>
                  <span>
                    {note.author?.fullName ?? 'Sistema'} · {formatShortDate(note.createdAt.slice(0, 10))}
                  </span>
                  {can('tenant:update') && (
                    <button
                      type="button"
                      className={styles.noteDelete}
                      onClick={() => remove.mutate(note.id)}
                    >
                      Borrar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña: actividad
// ---------------------------------------------------------------------------

const TONE_CLASS: Record<TimelineEntry['tone'], string> = {
  neutral: styles.timelineNeutral!,
  positive: styles.timelinePositive!,
  warning: styles.timelineWarning!,
  danger: styles.timelineDanger!,
};

function ActivityTab({ tenantId }: { tenantId: string }) {
  const query = useQuery({
    queryKey: queryKeys.tenantTimeline(tenantId),
    queryFn: () => api.tenantTimeline(tenantId),
  });

  if (query.isPending) return <Skeleton height={280} />;
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} inline />;
  }

  const entries = query.data.entries;

  return (
    <div className={styles.tabBody}>
      <Card>
        <CardHeader
          title="Historia"
          subtitle="Eventos de contrato, cobros, incidencias y documentos, en orden."
        />
        {entries.length === 0 ? (
          <EmptyState title="Sin actividad todavía" />
        ) : (
          <ol className={styles.timeline}>
            {entries.map((entry) => (
              <li key={entry.id} className={TONE_CLASS[entry.tone]}>
                <span className={styles.timelineDot} aria-hidden="true" />
                <div>
                  <span className={styles.timelineTitle}>{entry.title}</span>
                  {entry.detail && <span className={styles.timelineDetail}>{entry.detail}</span>}
                  <span className={styles.timelineDate}>{formatShortDate(entry.at.slice(0, 10))}</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
